/**
 * Booking enhancements — composer placeholder sync, auto-focus, and
 * location selection buttons.
 *
 * These hooks observe chat metadata events and adapt the input area to
 * guide the user through the booking flow.
 */

import { CHAT_CONVERSATION_RESET_EVENT } from '../../chatfe/chat/conversation-lifecycle.js';

import { DEFAULT_CHAT_PLACEHOLDER } from './config.js';
import { queryAllAcrossRoots } from './domUtils.js';
import { mountPhoneComposer, PHONE_COMPOSER_PLACEHOLDER } from './phoneComposer.js';

const PICKER_COMPOSER_PLACEHOLDER = 'Seçin veya yazın';
const REVIEW_COMPOSER_PLACEHOLDER = 'Seçim yapınız';
const REVIEW_ACTION_MESSAGES = {
  approve: 'Evet, onaylıyorum.',
  edit: 'Düzeltmek istiyorum.',
} as const;

/* ------------------------------------------------------------------ */
/*  Internal widget types (chat internals, not public API)            */
/* ------------------------------------------------------------------ */

interface InternalChatDrawer {
  inputEl?: HTMLTextAreaElement;
  scrollToBottomPresentation?: (behavior?: ScrollBehavior) => void;
}

interface InternalChatWidget {
  sendMessage?: (text: string) => void;
  sendAction?: (
    action: {
      title: string;
      type: string;
      payload?: Record<string, unknown>;
    },
    options?: { silent?: boolean }
  ) => void;
  on?: (
    event: 'message' | 'open' | 'close' | 'ready' | 'error' | 'context-update' | 'destroy',
    handler: (...args: unknown[]) => void
  ) => () => void;
  _drawer?: InternalChatDrawer;
}

interface ChatMetadataEventDetail {
  payload?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/*  Placeholder helpers                                               */
/* ------------------------------------------------------------------ */

function resolveMissingFieldPlaceholder(field: unknown): string {
  switch (field) {
    case 'full_name':
      return 'Adınız ve soyadınız';
    case 'phone':
      return PHONE_COMPOSER_PLACEHOLDER;
    case 'brand_model':
      return 'İlgilendiğiniz marka';
    case 'city':
      return 'Bulunduğunuz şehir';
    case 'location_id':
      return 'Tercih ettiğiniz şube';
    case 'request_subject':
      return 'Talebinizin konusu';
    default:
      return DEFAULT_CHAT_PLACEHOLDER;
  }
}

function resolveBookingPlaceholder(
  missingFields: unknown[],
  booking?: { fields?: { city?: unknown }; intent?: unknown; next_step?: unknown }
): string {
  switch (booking?.next_step) {
    case 'appointment_type':
      return 'Randevu türünü seçin';
    case 'city':
    case 'branch_selection':
    case 'brand_model':
      return PICKER_COMPOSER_PLACEHOLDER;
    case 'contact':
      return resolveMissingFieldPlaceholder(
        missingFields.includes('full_name') ? 'full_name' : 'phone'
      );
    case 'request_subject':
      return 'Talebinizin konusu';
  }

  if (missingFields[0] === 'location_id') {
    const fields = booking?.fields as { city?: unknown } | undefined;
    return typeof fields?.city === 'string' && fields.city.trim().length > 0
      ? 'Tercih ettiğiniz şube'
      : 'Şehir ve tercih ettiğiniz şube';
  }

  return resolveMissingFieldPlaceholder(missingFields[0]);
}

export function resolvePlaceholderFromMetadata(meta: Record<string, unknown>): string {
  const panel = meta.panel;
  if (!panel || typeof panel !== 'object') {
    return DEFAULT_CHAT_PLACEHOLDER;
  }

  const panelRecord = panel as Record<string, unknown>;
  const threadStateRaw = panelRecord.agent_thread_state;
  if (threadStateRaw && typeof threadStateRaw === 'object') {
    const threadState = threadStateRaw as {
      mode?: string;
      booking?: {
        intent?: string;
        missing_fields?: unknown[];
        fields?: { city?: unknown; datetime_clarification_required?: boolean };
        next_step?: unknown;
        review?: unknown;
      };
    };

    if (threadState.mode === 'otp_pending') {
      return 'SMS doğrulamayı karttan tamamlayın';
    }

    if (threadState.mode === 'booking_active') {
      const missing = Array.isArray(threadState.booking?.missing_fields)
        ? threadState.booking?.missing_fields
        : [];
      if (threadState.booking?.review && missing.length === 0) {
        return REVIEW_COMPOSER_PLACEHOLDER;
      }
      const booking = threadState.booking;
      if (booking?.intent === 'general_info' && !booking.fields?.datetime_clarification_required) {
        return resolveBookingPlaceholder(
          missing.filter((field) => field !== 'preferred_datetime'),
          {
            ...booking,
            next_step: booking.next_step === 'preferred_datetime' ? undefined : booking.next_step,
          }
        );
      }
      return resolveBookingPlaceholder(missing, booking);
    }
  }

  return DEFAULT_CHAT_PLACEHOLDER;
}

/* ------------------------------------------------------------------ */
/*  1. Composer placeholder sync                                      */
/* ------------------------------------------------------------------ */

export function mountBookingComposerPlaceholderSync(chat: unknown): () => void {
  const internalChat = chat as InternalChatWidget | null;
  if (!internalChat) {
    return () => {};
  }

  let currentPlaceholder = DEFAULT_CHAT_PLACEHOLDER;
  let retryTimerId: number | null = null;
  let remainingRetries = 0;
  let phoneInput: HTMLTextAreaElement | undefined;
  let removePhoneMask = () => {};
  let reviewChoiceMode = false;

  const applyPlaceholder = () => {
    const input = internalChat._drawer?.inputEl;
    const nextPhoneInput = currentPlaceholder === PHONE_COMPOSER_PLACEHOLDER ? input : undefined;
    if (phoneInput !== nextPhoneInput) {
      removePhoneMask();
      phoneInput = nextPhoneInput;
      removePhoneMask = phoneInput ? mountPhoneComposer(phoneInput) : () => {};
    }
    if (!input) return;
    if (input.placeholder !== currentPlaceholder) {
      input.placeholder = currentPlaceholder;
    }
    const shouldLock = currentPlaceholder === REVIEW_COMPOSER_PLACEHOLDER;
    if (reviewChoiceMode !== shouldLock) {
      reviewChoiceMode = shouldLock;
      input.readOnly = shouldLock;
      if (shouldLock && input.value) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
      }
    } else if (input.readOnly !== shouldLock) {
      input.readOnly = shouldLock;
    }
  };

  const scheduleApplyPlaceholder = (retries: number) => {
    if (retryTimerId !== null) {
      window.clearTimeout(retryTimerId);
      retryTimerId = null;
    }

    remainingRetries = retries;
    const tick = () => {
      applyPlaceholder();
      if (internalChat._drawer?.inputEl || remainingRetries <= 0) {
        retryTimerId = null;
        return;
      }
      remainingRetries -= 1;
      retryTimerId = window.setTimeout(tick, 120);
    };

    tick();
  };

  const onMetadata = (event: Event) => {
    const detail = (event as CustomEvent<ChatMetadataEventDetail>).detail;
    if (!detail?.payload || typeof detail.payload !== 'object') return;
    // Loading and other metadata do not replace the current booking/input state.
    if (!('panel' in detail.payload)) return;
    currentPlaceholder = resolvePlaceholderFromMetadata(detail.payload);
    scheduleApplyPlaceholder(8);
  };

  const onOpen = () => {
    scheduleApplyPlaceholder(12);
  };

  const onConversationReset = () => {
    currentPlaceholder = DEFAULT_CHAT_PLACEHOLDER;
    scheduleApplyPlaceholder(8);
  };

  window.addEventListener('gengage:chat:metadata', onMetadata as EventListener);
  window.addEventListener('gengage:chat:open', onOpen);
  window.addEventListener(CHAT_CONVERSATION_RESET_EVENT, onConversationReset);

  applyPlaceholder();

  return () => {
    removePhoneMask();
    if (internalChat._drawer?.inputEl?.readOnly && reviewChoiceMode) {
      internalChat._drawer.inputEl.readOnly = false;
    }
    if (retryTimerId !== null) {
      window.clearTimeout(retryTimerId);
    }
    window.removeEventListener('gengage:chat:metadata', onMetadata as EventListener);
    window.removeEventListener('gengage:chat:open', onOpen);
    window.removeEventListener(CHAT_CONVERSATION_RESET_EVENT, onConversationReset);
  };
}

/* ------------------------------------------------------------------ */
/*  2. Composer auto-focus                                            */
/* ------------------------------------------------------------------ */

function shouldAutoFocusComposer(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches === true;
  return window.innerWidth > 768 && !hasCoarsePointer;
}

export function mountBookingComposerAutoFocus(chat: unknown): () => void {
  const internalChat = chat as InternalChatWidget | null;
  if (!internalChat) {
    return () => {};
  }

  let retryTimerId: number | null = null;
  let mobileBlurTimerIds: number[] = [];
  let remainingRetries = 0;
  let mobileComposerUserInteracted = false;

  const clearMobileBlurTimers = () => {
    for (const timerId of mobileBlurTimerIds) {
      window.clearTimeout(timerId);
    }
    mobileBlurTimerIds = [];
  };

  const isInputFocused = (input: HTMLTextAreaElement): boolean => {
    const root = input.getRootNode();
    return (
      document.activeElement === input ||
      (root instanceof ShadowRoot && root.activeElement === input) ||
      input.matches(':focus')
    );
  };

  const blurMobileComposerIfNeeded = () => {
    if (shouldAutoFocusComposer() || mobileComposerUserInteracted) {
      return;
    }

    const input = internalChat._drawer?.inputEl;
    if (input && isInputFocused(input)) {
      input.blur();
    }
  };

  const scheduleMobileOpenBlur = () => {
    clearMobileBlurTimers();
    for (const delayMs of [0, 80, 240, 500]) {
      mobileBlurTimerIds.push(window.setTimeout(blurMobileComposerIfNeeded, delayMs));
    }
  };

  const markMobileComposerInteraction = (event: Event) => {
    if (shouldAutoFocusComposer()) {
      return;
    }

    const input = internalChat._drawer?.inputEl;
    const inputArea = input?.closest('[data-gengage-part="chat-input-area"]');
    if (!(input instanceof HTMLElement) || !(inputArea instanceof HTMLElement)) {
      return;
    }

    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (!path.includes(input) && !path.includes(inputArea)) {
      return;
    }

    mobileComposerUserInteracted = true;
    clearMobileBlurTimers();
  };

  const canFocusInput = (input: HTMLTextAreaElement | undefined): input is HTMLTextAreaElement => {
    if (!input || !input.isConnected || input.disabled) {
      return false;
    }

    const rect = input.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }

    const inputArea = input.closest('[data-gengage-part="chat-input-area"]');
    if (inputArea instanceof HTMLElement) {
      const areaRect = inputArea.getBoundingClientRect();
      if (areaRect.width <= 0 || areaRect.height <= 0) {
        return false;
      }
    }

    return true;
  };

  const applyFocus = () => {
    if (!shouldAutoFocusComposer()) {
      return true;
    }

    const input = internalChat._drawer?.inputEl;
    const root = input?.getRootNode();
    const focused = root instanceof ShadowRoot ? root.activeElement : document.activeElement;
    // Finishing a streamed reply must not close a picker the customer is using.
    if (focused instanceof HTMLElement && focused.closest('.otokoc-picker')) {
      return true;
    }
    const shell = input?.closest('[data-gengage-part="chat-input-shell"]');
    if (
      shell instanceof HTMLElement &&
      shell.dataset.otokocPickerMode === 'select' &&
      shell.querySelector('.otokoc-picker')
    ) {
      return true;
    }
    if (!canFocusInput(input)) {
      return false;
    }

    if (isInputFocused(input)) {
      return true;
    }

    input.focus({ preventScroll: true });
    const currentLength = input.value.length;
    try {
      input.setSelectionRange(currentLength, currentLength);
    } catch {
      /* ignore selection errors */
    }
    return isInputFocused(input);
  };

  const scheduleFocus = (retries: number) => {
    if (!shouldAutoFocusComposer()) {
      mobileComposerUserInteracted = false;
      if (retryTimerId !== null) {
        window.clearTimeout(retryTimerId);
        retryTimerId = null;
      }
      scheduleMobileOpenBlur();
      return;
    }

    clearMobileBlurTimers();
    if (retryTimerId !== null) {
      window.clearTimeout(retryTimerId);
      retryTimerId = null;
    }

    remainingRetries = retries;
    const tick = () => {
      if (applyFocus() || remainingRetries <= 0) {
        retryTimerId = null;
        return;
      }
      remainingRetries -= 1;
      retryTimerId = window.setTimeout(tick, 120);
    };

    tick();
  };

  const onOpen = () => {
    scheduleFocus(12);
  };

  const stopMessageListener =
    typeof internalChat.on === 'function'
      ? internalChat.on('message', () => {
          if (shouldAutoFocusComposer()) {
            scheduleFocus(8);
          }
        })
      : () => {};

  window.addEventListener('gengage:chat:open', onOpen);
  window.addEventListener('pointerdown', markMobileComposerInteraction as EventListener, {
    capture: true,
  });
  window.addEventListener('touchstart', markMobileComposerInteraction as EventListener, {
    capture: true,
    passive: true,
  });
  window.addEventListener('mousedown', markMobileComposerInteraction as EventListener, {
    capture: true,
  });
  scheduleFocus(8);

  return () => {
    if (retryTimerId !== null) {
      window.clearTimeout(retryTimerId);
    }
    clearMobileBlurTimers();
    stopMessageListener();
    window.removeEventListener('gengage:chat:open', onOpen);
    window.removeEventListener('pointerdown', markMobileComposerInteraction as EventListener, true);
    window.removeEventListener('touchstart', markMobileComposerInteraction as EventListener, true);
    window.removeEventListener('mousedown', markMobileComposerInteraction as EventListener, true);
  };
}

/* ------------------------------------------------------------------ */
/*  3. Location selection buttons                                     */
/* ------------------------------------------------------------------ */

function extractBookingLocationIdFromClasses(card: HTMLElement): string {
  const token = Array.from(card.classList).find((className) =>
    className.startsWith('otokoc-location-id--')
  );
  return token ? token.slice('otokoc-location-id--'.length).trim() : '';
}

export function mountBookingLocationSelection(chat: unknown): () => void {
  const internalChat = chat as InternalChatWidget | null;
  if (!internalChat?.sendAction && !internalChat?.sendMessage) {
    return () => {};
  }

  let bookingSelectionActive = false;
  let rafId: number | null = null;
  let currentScrollEl: HTMLElement | null = null;
  let detachScrollObserver = () => {};

  const removeInjectedButtons = () => {
    for (const button of queryAllAcrossRoots('[data-otokoc-fe-booking-select="1"]')) {
      button.remove();
    }
    for (const card of queryAllAcrossRoots('[data-otokoc-fe-booking-card="1"]')) {
      delete card.dataset.otokocFeBookingCard;
    }
  };

  const scheduleEnhancement = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(() => {
      rafId = null;
      enhanceLocationCards();
    });
  };

  const getCardBranchName = (card: HTMLElement): string => {
    const dataName = card.dataset.otokocBranchName?.trim();
    if (dataName) {
      return dataName;
    }
    const heading = Array.from(card.querySelectorAll('p')).find((node) => {
      const text = node.textContent?.trim() ?? '';
      return text.length > 0;
    });
    return heading?.textContent?.trim() ?? '';
  };

  const getCardLocationId = (card: HTMLElement): string =>
    card.dataset.otokocLocationId?.trim() || extractBookingLocationIdFromClasses(card);

  const getCardActionRow = (card: HTMLElement): HTMLElement => {
    const bookingRow = card.querySelector<HTMLElement>('[data-otokoc-fe-booking-actions="1"]');
    if (bookingRow) {
      return bookingRow;
    }

    const existing = Array.from(card.querySelectorAll('div')).find((node) =>
      Array.from(node.querySelectorAll('a')).some((anchor) =>
        /Haritada Aç/i.test(anchor.textContent ?? '')
      )
    );

    if (existing instanceof HTMLElement) {
      return existing;
    }

    const row = document.createElement('div');
    row.dataset.otokocFeBookingActions = '1';
    row.style.marginTop = '12px';
    card.appendChild(row);
    return row;
  };

  const findLocationCards = (): HTMLElement[] => queryAllAcrossRoots('.otokoc-location-card');

  const enhanceLocationCards = () => {
    if (!bookingSelectionActive) {
      removeInjectedButtons();
      return;
    }

    for (const card of findLocationCards()) {
      const branchName = getCardBranchName(card);
      const locationId = getCardLocationId(card);
      if (!branchName || !locationId) continue;

      card.dataset.otokocFeBookingCard = '1';
      const actionsRow = getCardActionRow(card);
      const existingButton = actionsRow.querySelector<HTMLElement>(
        '[data-otokoc-fe-booking-select="1"]'
      );
      if (existingButton) {
        existingButton.dataset.otokocLocationName = branchName;
        existingButton.dataset.otokocLocationId = locationId;
        existingButton.textContent = 'Bu Şubeyi Seç';
        continue;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.otokocFeBookingSelect = '1';
      button.dataset.otokocLocationName = branchName;
      button.dataset.otokocLocationId = locationId;
      button.textContent = 'Bu Şubeyi Seç';
      Object.assign(button.style, {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '8px',
        marginLeft: '8px',
        padding: '10px 14px',
        borderRadius: '999px',
        background: '#111827',
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: '700',
        border: '0',
        cursor: 'pointer',
      });
      actionsRow.appendChild(button);
    }
  };

  const attachScrollObserver = (scrollEl: HTMLElement): (() => void) => {
    const handleClick = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const button = target.closest<HTMLElement>('[data-otokoc-fe-booking-select="1"]');
      if (!button) {
        return;
      }
      const branchName = button.dataset.otokocLocationName?.trim();
      const locationId = button.dataset.otokocLocationId?.trim();
      if (!branchName || !locationId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (typeof internalChat.sendAction === 'function') {
        internalChat.sendAction({
          title: branchName,
          type: 'inputText',
          payload: {
            text: branchName,
            location_id: locationId,
            branch_name: branchName,
          },
        });
        return;
      }
      internalChat.sendMessage?.(branchName);
    };

    const observer = new MutationObserver(() => {
      scheduleEnhancement();
    });
    observer.observe(scrollEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    scrollEl.addEventListener('click', handleClick);
    return () => {
      observer.disconnect();
      scrollEl.removeEventListener('click', handleClick);
    };
  };

  const attachIfNeeded = () => {
    const nextScrollEl = queryAllAcrossRoots('#gengage-chat-scroll')[0] ?? null;
    if (!nextScrollEl && currentScrollEl) {
      detachScrollObserver();
      detachScrollObserver = () => {};
      currentScrollEl = null;
      return;
    }
    if (!nextScrollEl || nextScrollEl === currentScrollEl) {
      return;
    }
    detachScrollObserver();
    currentScrollEl = nextScrollEl;
    detachScrollObserver = attachScrollObserver(nextScrollEl);
    scheduleEnhancement();
  };

  const syncFromMetadata = (payload: Record<string, unknown>) => {
    const panel = payload.panel;
    if (!panel || typeof panel !== 'object') {
      bookingSelectionActive = false;
      scheduleEnhancement();
      return;
    }

    const panelRecord = panel as Record<string, unknown>;
    const threadStateRaw = panelRecord.agent_thread_state;
    if (!threadStateRaw || typeof threadStateRaw !== 'object') {
      bookingSelectionActive = false;
      scheduleEnhancement();
      return;
    }

    const threadState = threadStateRaw as {
      mode?: string;
      booking?: { missing_fields?: unknown[] };
    };
    const missing = Array.isArray(threadState.booking?.missing_fields)
      ? threadState.booking?.missing_fields
      : [];

    bookingSelectionActive =
      threadState.mode === 'booking_active' && missing.includes('location_id');
    scheduleEnhancement();
  };

  const onMetadata = (event: Event) => {
    const detail = (event as CustomEvent<ChatMetadataEventDetail>).detail;
    if (!detail?.payload || typeof detail.payload !== 'object') return;
    syncFromMetadata(detail.payload);
  };

  const stopMessageListener =
    typeof internalChat.on === 'function'
      ? internalChat.on('message', () => {
          scheduleEnhancement();
        })
      : () => {};
  attachIfNeeded();
  const hostObserver = new MutationObserver(() => {
    attachIfNeeded();
  });
  hostObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('gengage:chat:metadata', onMetadata as EventListener);
  scheduleEnhancement();

  return () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
    detachScrollObserver();
    hostObserver.disconnect();
    stopMessageListener();
    removeInjectedButtons();
    window.removeEventListener('gengage:chat:metadata', onMetadata as EventListener);
  };
}

/* ------------------------------------------------------------------ */
/*  4. Review approval buttons                                        */
/* ------------------------------------------------------------------ */

export function mountBookingReviewActions(chat: unknown): () => void {
  const internalChat = chat as InternalChatWidget | null;
  if (
    (!internalChat?.sendAction && !internalChat?.sendMessage) ||
    typeof document === 'undefined' ||
    typeof MutationObserver === 'undefined'
  ) {
    return () => {};
  }

  let reviewActive = false;
  let composerActions: HTMLElement | null = null;
  let hiddenInputShell: HTMLElement | null = null;
  let restoreInputShellStyles: {
    display: string;
    displayPriority: string;
  } | null = null;

  const sendReviewAction = (message: string, title: string) => {
    if (typeof internalChat.sendAction === 'function') {
      internalChat.sendAction({
        title,
        type: 'inputText',
        payload: { text: message },
      });
      return;
    }

    internalChat.sendMessage?.(message);
  };

  const syncReviewButtonOrder = () => {
    const edit = composerActions?.querySelector<HTMLElement>(
      '[data-otokoc-booking-review-composer-action="secondary"]'
    );
    const approve = composerActions?.querySelector<HTMLElement>(
      '[data-otokoc-booking-review-composer-action="primary"]'
    );
    if (edit) {
      edit.style.order = '1';
    }
    if (approve) {
      approve.style.order = '2';
    }
  };

  const createReviewButton = (
    label: string,
    message: string,
    variant: 'primary' | 'secondary'
  ): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.otokocBookingReviewComposerAction = variant;
    button.textContent = label;
    Object.assign(button.style, {
      order: variant === 'secondary' ? '1' : '2',
      flex: '1 1 0',
      minWidth: '0',
      minHeight: '42px',
      padding: '10px 12px',
      borderRadius: '999px',
      border: variant === 'primary' ? '1px solid #0b224a' : '1px solid #b7c4d8',
      background: variant === 'primary' ? '#0f2b5b' : '#ffffff',
      color: variant === 'primary' ? '#ffffff' : '#0f2b5b',
      fontSize: '14px',
      fontWeight: '700',
      lineHeight: '1.2',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    });
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      for (const actionButton of Array.from(
        composerActions?.querySelectorAll<HTMLButtonElement>('button') || []
      )) {
        actionButton.disabled = true;
        actionButton.style.opacity = '0.72';
        actionButton.style.cursor = 'default';
      }
      sendReviewAction(message, label);
      if (variant === 'secondary') {
        reviewActive = false;
        removeComposerActions();
      }
    });
    return button;
  };

  const findInputShell = (inputArea: HTMLElement): HTMLElement | null => {
    const input = internalChat._drawer?.inputEl;
    return (
      input?.closest<HTMLElement>('[data-gengage-part="chat-input-shell"]') ||
      inputArea.querySelector<HTMLElement>('[data-gengage-part="chat-input-shell"]') ||
      inputArea.querySelector<HTMLElement>('.gengage-chat-input-pill')
    );
  };

  const findComposerActionsTarget = (): {
    inputArea: HTMLElement;
    inputShell: HTMLElement | null;
  } | null => {
    const input = internalChat._drawer?.inputEl;
    const inputArea = input?.closest<HTMLElement>('[data-gengage-part="chat-input-area"]');
    if (!inputArea) {
      return null;
    }

    return { inputArea, inputShell: findInputShell(inputArea) };
  };

  const removeComposerActions = () => {
    composerActions?.remove();
    composerActions = null;
    if (restoreInputShellStyles && hiddenInputShell) {
      const { display, displayPriority } = restoreInputShellStyles;
      if (display) {
        hiddenInputShell.style.setProperty('display', display, displayPriority);
      } else {
        hiddenInputShell.style.removeProperty('display');
      }
      restoreInputShellStyles = null;
    }
    hiddenInputShell = null;
    const input = internalChat._drawer?.inputEl;
    if (input?.readOnly) {
      input.readOnly = false;
    }
  };

  const renderComposerActions = () => {
    if (!reviewActive) {
      removeComposerActions();
      return;
    }

    const target = findComposerActionsTarget();
    if (!target) {
      return;
    }
    const { inputArea, inputShell } = target;

    if (composerActions && composerActions.parentElement === inputArea) {
      syncReviewButtonOrder();
      return;
    }

    removeComposerActions();
    if (inputShell) {
      restoreInputShellStyles = {
        display: inputShell.style.display,
        displayPriority: inputShell.style.getPropertyPriority('display'),
      };
      inputShell.style.setProperty('display', 'none', 'important');
      hiddenInputShell = inputShell;
    }

    const actions = document.createElement('div');
    actions.dataset.otokocBookingReviewComposerActions = '1';
    Object.assign(actions.style, {
      display: 'flex',
      gap: '8px',
      width: '100%',
      padding: '0 4px 10px',
      boxSizing: 'border-box',
    });
    actions.append(
      createReviewButton('Düzeltmek istiyorum', REVIEW_ACTION_MESSAGES.edit, 'secondary'),
      createReviewButton('Onaylıyorum', REVIEW_ACTION_MESSAGES.approve, 'primary')
    );
    composerActions = actions;
    syncReviewButtonOrder();
    inputArea.insertBefore(actions, inputShell ?? inputArea.firstChild);
  };

  const syncFromMetadata = (payload: Record<string, unknown>) => {
    const panel = payload.panel;
    const threadStateRaw =
      panel && typeof panel === 'object'
        ? (panel as Record<string, unknown>).agent_thread_state
        : undefined;
    const threadState =
      threadStateRaw && typeof threadStateRaw === 'object'
        ? (threadStateRaw as {
            mode?: string;
            booking?: { missing_fields?: unknown[]; review?: unknown };
          })
        : undefined;
    const missing = Array.isArray(threadState?.booking?.missing_fields)
      ? threadState.booking.missing_fields
      : [];
    reviewActive =
      threadState?.mode === 'booking_active' &&
      Boolean(threadState.booking?.review) &&
      missing.length === 0;
    renderComposerActions();
  };

  const onMetadata = (event: Event) => {
    const detail = (event as CustomEvent<ChatMetadataEventDetail>).detail;
    if (!detail?.payload || typeof detail.payload !== 'object') return;
    if (!('panel' in detail.payload)) return;
    syncFromMetadata(detail.payload);
  };

  const onConversationReset = () => {
    reviewActive = false;
    removeComposerActions();
  };

  renderComposerActions();
  const hostObserver = new MutationObserver(() => {
    renderComposerActions();
  });
  hostObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  window.addEventListener('gengage:chat:metadata', onMetadata as EventListener);
  window.addEventListener(CHAT_CONVERSATION_RESET_EVENT, onConversationReset);

  return () => {
    removeComposerActions();
    hostObserver.disconnect();
    window.removeEventListener('gengage:chat:metadata', onMetadata as EventListener);
    window.removeEventListener(CHAT_CONVERSATION_RESET_EVENT, onConversationReset);
  };
}
